import { CommonModule, DatePipe, NgForOf, NgIf } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  FormsModule,
} from '@angular/forms';
import {
  TuiAlertService,
  TuiAppearance,
  TuiButton,
  TuiDataList,
  TuiDropdown,
  TuiHint,
  TuiLink,
  TuiLoader,
  tuiLoaderOptionsProvider,
  TuiTextfield,
} from '@taiga-ui/core';
import {
  TuiBreadcrumbs,
  TuiDataListDropdownManager,
  TuiDataListWrapper,
  TuiFade,
  TuiItemsWithMore,
  TuiPagination,
  TuiStatus,
  TuiStringifyContentPipe,
  TuiSwitch,
  TuiTabs,
} from '@taiga-ui/kit';
import { TuiTable } from "@taiga-ui/addon-table";
import { TuiComboBoxModule, TuiInputSliderModule, TuiTextfieldControllerModule } from '@taiga-ui/legacy';
import { RouterLink } from '@angular/router';
import { TuiCell, TuiNavigation, TuiSearch } from "@taiga-ui/layout";
import { ClientService } from '../../core/services/client.service';
import { NurseService } from '../../core/services/nurse.service';
import { ProcedureRecordService } from '../../core/services/procedure-record.service';
import { DisableUnlessPermissionDirective } from '../../core/directives/disable-unless-permission.directive';
import { loadListState, saveListState } from '../../shared/utils/list-state';

@Component({
  selector: 'app-procedure',
  standalone: true,
  imports: [
    DisableUnlessPermissionDirective,
    FormsModule,
        NgForOf,
        NgIf,
        TuiAppearance,
        TuiDataListWrapper,
        TuiBreadcrumbs,
        TuiButton,
        TuiDataList,
        TuiPagination,
        TuiDropdown,
        TuiFade,
        TuiTable,
        TuiLink,
        TuiNavigation,
        TuiTabs,
        TuiTextfield,
        TuiStatus,
        TuiItemsWithMore,
        TuiSearch,
        ReactiveFormsModule,
        CommonModule,
        TuiInputSliderModule,
        TuiTextfieldControllerModule,
        RouterLink,
        TuiComboBoxModule,
        TuiLoader,
        TuiHint,
        TuiStringifyContentPipe,
  ],
  templateUrl: './procedure.component.html',
  styleUrl: './procedure.component.scss',
  providers: [DatePipe, tuiLoaderOptionsProvider({ size: 'l' })],
})
export class ProcedureComponent implements OnInit {
  protected form = new FormGroup({
    search: new FormControl(''),
    nurse: new FormControl(''),
    client: new FormControl(''),
    clientType: new FormControl(''),
    startDate: new FormControl(''),
    endDate: new FormControl(''),
  });

  protected readonly breadcrumbs = ["Royal Hospital", "Extra Procedures"];
  protected readonly sizes = ["m"] as const;
  protected size = this.sizes[0];
  procedures: any[] = [];
  nurses: any[] = [];
  clients: any[] = [];
  protected readonly clientTypeItems = [
    "",
    "Registered",
    "Non Registered",
    // "Inward",
  ];

  /**
   * The combo box renders an empty string as a blank row. Map "" → "All Types"
   * so the user can see/pick the "no filter" option in the dropdown and the
   * closed combo box label stays meaningful when the filter is cleared.
   */
  protected stringifyClientType = (value: string): string =>
    value ? value : "All Types";

  isLoading = false;
  total = 0;
  pageSize = 10;
  pageIndex = 1;
  /**
   * Soft-warn flexibility: when on, the paged query also returns archived
   * (soft-deleted) procedure records so users with PROCEDURE_UPDATE can
   * restore them. The Restore button is shown only on rows where
   * archived=true.
   */
  includeArchived: boolean = false;

  /**
   * Walk-in view: hides Patient Type filter, forces clientType=WalkIn in the
   * paged payload, swaps client column to walk-in name/contact, hides invoice
   * link, and points Edit at the dedicated walk-in route. Driven by route
   * data `walkIn: true`.
   */
  isWalkInMode = false;

  private procedureService = inject(ProcedureRecordService);
  private clientService = inject(ClientService);
  private nurseService = inject(NurseService);
  private alertService = inject(TuiAlertService);
  private route = inject(ActivatedRoute);

  private readonly listStateKey = "procedure-list";
  private readonly walkInListStateKey = "walk-in-extra-charges-list";

  ngOnInit(): void {
    this.isWalkInMode = !!this.route.snapshot.data?.["walkIn"];
    this.restoreListState();
    this.loadProcedures();
    if (!this.isWalkInMode) {
      // The walk-in view never uses the client/nurse filters in its toolbar
      // (see template ngIf), so skip these network calls entirely there.
      this.loadClients();
      this.loadNurses();
    }
  }

  private get activeListStateKey(): string {
    return this.isWalkInMode ? this.walkInListStateKey : this.listStateKey;
  }

  private restoreListState(): void {
    const restored = loadListState(this.activeListStateKey, {
      formValue: {} as any,
      pageIndex: this.pageIndex,
      pageSize: this.pageSize,
      includeArchived: this.includeArchived,
    });
    if (restored.formValue) this.form.patchValue(restored.formValue);
    this.pageIndex = restored.pageIndex;
    this.pageSize = restored.pageSize;
    this.includeArchived = restored.includeArchived;
    if (this.isWalkInMode) {
      this.includeArchived = false;
    }
  }

  private rememberListState(): void {
    saveListState(this.activeListStateKey, {
      formValue: this.form.value,
      pageIndex: this.pageIndex,
      pageSize: this.pageSize,
      includeArchived: this.isWalkInMode ? false : this.includeArchived,
    });
  }

  loadProcedures(): void {
    // this.rememberListState();
    const formValue = this.form.value;
    // Walk-in view is hard-pinned to clientType=WalkIn; the user can't widen
    // the filter from this screen because the toolbar control is hidden.
    const resolvedClientType = this.isWalkInMode
      ? "WalkIn"
      : formValue.clientType === "Non Registered"
        ? "Unregistered"
        : formValue.clientType || undefined;
    const payload = {
      pageIndex: this.pageIndex,
      pageSize: this.pageSize,
      search: formValue.search,
      nurse: formValue.nurse,
      client: formValue.client,
      clientType: resolvedClientType,
      startDate: formValue.startDate,
      endDate: formValue.endDate,
      includeArchived: this.isWalkInMode ? false : this.includeArchived,
    };

    this.isLoading = true;

    this.procedureService.getPagedProcedures(payload).subscribe({
      next: (res: any) => {
        if (res.data) {
          this.procedures = res.data;
          this.total = res.metadata.total;

          console.log('Procedures loaded:', this.procedures);
          console.log('Total procedures:', this.total);
        } else {
          this.procedures = [];
          this.total = 0;
        }
      },
      error: (err) => {
        console.error('Error fetching procedures:', err);
        this.alertService.open('Failed to load procedures.', {
          appearance: 'error',
          label: 'Error',
        }).subscribe();
      },
      complete: () => (this.isLoading = false),
    });
  }

  loadClients(): void {
    this.clientService.getAllClients().subscribe({
      next: (res: any) => {
        const clients = Array.isArray(res) ? res : [];
        this.clients = clients.filter((c: any) => !c?.archived && c?.active !== false);
      },
      error: (err) => console.error('Error fetching clients:', err),
    });
  }

  loadNurses(): void {
    this.nurseService.getAllNurses().subscribe({
      next: (res: any) => {
        const nurses = Array.isArray(res) ? res : [];
        this.nurses = nurses.filter((n: any) => !n?.archived && n?.active !== false);
      },
      error: (err) => console.error('Error fetching nurses:', err),
    });
  }

  onPageChange(page: number): void {
    this.pageIndex = page;
    this.loadProcedures();
  }

  applyFilters(): void {
    this.pageIndex = 1;
    this.loadProcedures();
  }

  onSearchEnter(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      this.applyFilters();
    }
  }

  clearFilters(): void {
    // Reset to empty strings so the combo box/textfield clear visually and
    // the backend payload sends "" rather than null. (form.reset() with no
    // args sets every control to null which can confuse downstream filters.)
    this.form.reset({
      search: '',
      nurse: '',
      client: '',
      clientType: '',
      startDate: '',
      endDate: '',
    });
    this.includeArchived = false;
    this.pageIndex = 1;
    this.loadProcedures();
  }

  toggleIncludeArchived(value: boolean): void {
    this.includeArchived = value;
    this.pageIndex = 1;
    this.loadProcedures();
  }

  restoreProcedure(record: any): void {
    if (!record?._id) return;
    this.procedureService.unarchiveProcedure(record._id).subscribe({
      next: () => {
        this.alertService
          .open("Procedure restored.", {
            label: "Success",
            appearance: "success",
          })
          .subscribe();
        this.loadProcedures();
      },
      error: () => {
        this.alertService
          .open("Failed to restore procedure.", {
            label: "Error",
            appearance: "error",
          })
          .subscribe();
      },
    });
  }

  getPageCount(): number {
    return Math.ceil(this.total / this.pageSize);
  }

  getProceduresDisplay(record: any): string {
    if (record.procedures && Array.isArray(record.procedures) && record.procedures.length > 0) {
      return record.procedures.join(', ');
    }
    // Fallback for old data structure
    return record.procedure || 'N/A';
  }

  getGrandTotal(record: any): number {
    return record.grandTotal || 0;
  }

  getNurseName(record: any): string {
    // Prioritize new nurseName field, fallback to old nurse structure
    if (record.nurseName) {
      return record.nurseName;
    }
    return record.nurse?.name || 'N/A';
  }

  /**
   * Monthly invoice / charges screen for the row's client and the month of `record.date`.
   * Returns null when client or date is missing (no link). Walk-in records have
   * no Client document, so this returns null — `walkInInvoiceLink` handles them.
   */
  invoiceChargesLink(record: any): string[] | null {
    if (this.isWalkInMode || record?.clientType === "WalkIn") return null;
    const cid = record?.client?._id;
    if (!cid) return null;
    const d = record?.date ? new Date(record.date) : null;
    if (!d || Number.isNaN(d.getTime())) return null;
    const month = d.getMonth() + 1;
    const year = d.getFullYear();
    return ['/invoice', String(cid), String(month), String(year)];
  }

  /**
   * Single-record invoice route for a walk-in procedure. Returns null when
   * the row isn't a walk-in (regular clients use the monthly invoice).
   */
  walkInInvoiceLink(record: any): string[] | null {
    const isWalkIn = this.isWalkInMode || record?.clientType === "WalkIn";
    if (!isWalkIn || !record?._id) return null;
    return ['/walk-in-invoice', String(record._id)];
  }

  /**
   * Display label for the Client column. Falls back to walk-in fields when
   * the row is a walk-in (no joined client document); regular rows keep
   * the existing `record.client?.name` value.
   */
  getClientDisplay(record: any): string {
    // If it's a walk-in record, display the manually entered walk-in name
    if (record?.clientType === "WalkIn" || !record?.client) {
      return record?.walkInClientName?.trim() || "Walk-in";
    }
    
    // Prioritize the guardian's name, fallback to client name, then to 'N/A'
    return record.client?.guardianName?.trim() || record.client?.name || 'N/A';
  }
  /**
   * Display label for the Patient Type column. Walk-in rows are stored as
   * "WalkIn" on the procedure document (no joined client.clientType).
   */
  getClientTypeDisplay(record: any): string {
    const procedureLevel = record?.clientType;
    if (procedureLevel === "WalkIn") return "Walk-in";
    if (record?.client?.clientType == "Unregistered") return "Non Registered"
    return record?.client?.clientType || procedureLevel || '—';
  }

  /**
   * Edit route depends on mode: walk-in rows go to the dedicated walk-in
   * editor so the form re-enters walk-in mode and skips client selection.
   */
  getEditRoute(record: any): string[] {
    const isWalkIn = this.isWalkInMode || record?.clientType === "WalkIn";
    return isWalkIn
      ? ['/admin/walk-in-extra-charges/add-edit', record._id]
      : ['/admin/procedures/add-edit', record._id];
  }

  getPaymentBreakdown(record: any): string {
    if (!record.payments && !record.customPayments) {
      return 'No payment details available';
    }
    let breakdown = 'Payment Breakdown:\n\n';
    if (record.payments) {
      const standardPayments = [
        { name: 'Doctor Payment', value: record.payments.doctorPayment },
        { name: 'Nursing Payment', value: record.payments.nursingPayment },
        { name: 'Pharmacy Payment', value: record.payments.pharmacyPayment },
        { name: 'Laboratory Payment', value: record.payments.laboratoryPayment },
        { name: 'Ambulance Payment', value: record.payments.ambulancePayment },
        { name: 'Physiotherapy Payment', value: record.payments.physiotherapyPayment },
        { name: 'Other Payment', value: record.payments.otherPayment }
      ];
      standardPayments.forEach(payment => {
        if (payment.value && payment.value > 0) {
          breakdown += `${payment.name}: LKR ${payment.value}\n`;
        }
      });
    }
    if (record.customPayments && record.customPayments.length > 0) {
      breakdown += '\nCustom Payments:\n';
      record.customPayments.forEach((customPayment: any) => {
        if (customPayment.value && customPayment.value > 0) {
          breakdown += `${customPayment.name}: LKR ${customPayment.value}\n`;
        }
      });
    }
    breakdown += `\nGrand Total: LKR ${record.grandTotal || 0}`;
    return breakdown;
  }
}
